import { Page, Text, View, Document, Font, Image } from '@react-pdf/renderer';
import { RESULTS_COLORS } from './assets';
import { ResultsView, NO_ARTICLE_RESULTS } from './Results';
import { TYPE_DESCRIPTIONS } from '../../results';
import { SVGRenderer } from '../shared/SVGRenderer';

Font.register({ family: 'Omnes', src: '/fonts/Omnes Regular.ttf', fontWeight: 400 });
Font.register({ family: 'Omnes', src: '/fonts/Omnes SemiBold.ttf', fontWeight: 700 });
Font.register({ family: 'Omnes', src: '/fonts/Omnes SemiBold Italic.ttf', fontWeight: 700, fontStyle: 'italic' });

export function PDFResultText({
  result,
  resultView,
  headerText
}: {
  result: string;
  resultView: ResultsView;
  headerText: string;
}) {
  const isVowel = "aeiou".includes(result[0].toLowerCase());
  const paragraphs = TYPE_DESCRIPTIONS[result.toLowerCase()].split('\n');
  
  return (
      <View style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12pt',
        zIndex: 10,
      }}>
        <View
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            paddingVertical: '20pt',
            gap: '10pt',
            position: 'relative',
            marginBottom: '20pt',
            backgroundColor: RESULTS_COLORS[result.toLowerCase()].background
          }}
        >
          <Text style={{
            fontWeight: 600,
            paddingHorizontal: '6pt',
            paddingBottom: '4pt',
            fontSize: '12pt',
            borderBottom: '1pt',
            borderColor: '#4C4D55',
            color: '#4C4D55'
          }}>
            {headerText}
          </Text>
          <Image
            src={`/results/${result}/shape.png`}
            style={{ zIndex: 10, width: '100pt', position: 'absolute', left: '-20pt', top: '-40pt' }}
          />
          <SVGRenderer
            style={{ width: '200pt' }}
            src={`/results/${result.toLowerCase()}/crayon-md.svg`}
          />
          <Text
            style={{
              fontSize: '24pt',
              fontWeight: 700,
              fontStyle: 'italic',
              color: RESULTS_COLORS[result.toLowerCase()].title
            }}
          >
            {"You Are " + (NO_ARTICLE_RESULTS.includes(result) ? "" : "A" + (isVowel ? "n " : " ")) + result}
          </Text>
          <View style={{ position: "relative", width: '100%', display: 'flex', alignItems: 'center' }}>
            {resultView === ResultsView.Engage && <Text style={{ width: '75%', fontSize: '10pt' }}>
              This scale in the Crayola Creativity Profile looks at your preferred way of engaging in the creative process. The scale ranges from a more reflective style to a more transformative, hands-on style. A Reflect orientation refers to individuals who develop creative breakthroughs primarily by stepping back and reflecting, while those with a Transform orientation develop creative insight by actively working through challenges and opportunities. 
              {'\n\n'}To initiate your personal journey of creative discovery, read your preference summary. To facilitate insight and self-awareness, we encourage you to make note of the aspects of this description that you feel fit you. 
            </Text>}
            {resultView === ResultsView.Perceive && <Text style={{ width: '75%', fontSize: '10pt' }}>
              This scale on the Crayola Creativity Profile looks at your preferred way of looking at the world. This dimension ranges from an orientation referred to as Distinction, which is a creative style of perceiving distinctions and details, to Connection on the other end, which is a tendency to focus on patterns and connections.   
              {'\n\n'}To initiate your personal journey of creative discovery, read your preference summary. To facilitate insight and self-awareness, we encourage you to make note of the aspects of this description that you feel fit you. 
            </Text>}
            {resultView === ResultsView.Think && <Text style={{ width: '75%', fontSize: '10pt' }}>
              This scale looks at your preferred creative-thinking style, which ranges from a Divergent orientation, a tendency to think in broad and exploratory ways, to a Convergent orientation, a tendency to think in focused and analytical ways. 
              {'\n\n'}To initiate your personal journey of creative discovery, read your preference summary. To facilitate insight and self-awareness, we encourage you to make note of the aspects of this description that you feel fit you.
            </Text>}
            <Image
              src={`/results/${result.toLowerCase()}/circle.png`}
              style={{ position: 'absolute', bottom: '-35pt', right: '20pt', height: '40pt', width: '40pt', zIndex: 9999 }}
            />
          </View>
        </View>
        {paragraphs.map((paragraph, i) => (
            <View
              key={`result-${i}`}
              wrap={false}
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10pt',
                alignItems: 'flex-start',
                marginBottom: '15pt',
                width: '80%'
              }}
            >
              <SVGRenderer
                src={`/results/${result.toLowerCase()}/crayon-sm.svg`}
                style={{ width: '40pt', position: 'relative', bottom: '3pt' }}
              />
              <Text style={{ fontSize: '10pt', width: '90%' }}>{paragraph}</Text>
            </View>
        ))}
      </View>
  );
}

export function PDFViewControl({result, headerText}: {result: string; headerText: string;}) {
  return (
    <View
      style={{
        width: '175pt',
        height: '105pt',
        backgroundColor: 'white',
        border: `3pt solid ${RESULTS_COLORS[result.toLowerCase()].stroke}`,
        position: 'relative',
        borderRadius: '16pt',
        display: 'flex',
        flexDirection: 'column',
        gap: '4pt',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
        <Text style={{ fontSize: '12pt', fontWeight: 700, fontStyle: 'italic', color: '#76767d'}}>{headerText}</Text>
        <Text
          style={{
            fontWeight: 700,
            fontSize: result === 'Detail-Oriented' ? '18pt': '20pt',
            textTransform: 'uppercase',
            position: 'relative',
            bottom: '4pt',
            paddingVertical: result === 'Detail-Oriented' ? '2pt' : 0
          }}
        >
            {result}
        </Text>
        <SVGRenderer
            style={{ width: '85%', position: 'relative', bottom: '6pt' }}
            src={`/results/${result.toLowerCase()}/stroke.svg`}
        />
        <SVGRenderer
            style={{ width: '150pt', position: 'relative', bottom: '4pt'}}
            src={`/results/${result.toLowerCase()}/crayon-md.svg`}
        />
    </View>
  )
};

export function ResultsPDF({
  thinkType,
  engageType,
  perceiveType
}: {
  thinkType: string;
  engageType: string;
  perceiveType: string;
}) {
  const date = new Date();
  const dateString = date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <Document>
      <Page size="A4" style={{
        fontFamily: 'Omnes',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        paddingBottom: 60,
        gap: '10pt',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Image src='/pdf/green-shape.png' style={{ width: '100%', position: 'absolute', top: '0px' }}/>
        <Image src='/pdf/blue-shape.png' style={{ width: '100%', position: 'absolute', bottom: '0px' }}/>
        <Image src='/pdf/OrangeCircle.png' style={{width: '250px', position: 'absolute', top: '-100px', left: '-10px'}} />
        <SVGRenderer src='/pdf/Ellipse 2.svg' style={{width: '50pt', position: 'absolute', bottom: '100pt', right: '40pt'}} />
        <SVGRenderer src='/pdf/PinkCircle.svg' style={{width: '150pt', position: 'absolute', bottom: '-170pt', right: '70pt'}} />
        <SVGRenderer src='/pdf/CrayolaLogo_White.svg' style={{width: '150pt', position: 'absolute', bottom: '-25pt', left: '230pt'}} />
        <Text style={{
          width: '60%',
          color: '#015ca7',
          fontSize: '48pt',
          letterSpacing: '1.5pt',
          fontStyle: 'italic',
          textAlign: 'center',
          fontWeight: 600,
          lineHeight: '1pt'
        }}>
          Your Creativity Profile
        </Text>
        <SVGRenderer src='/green-brush.svg' style={{ width: '200pt', position: 'relative' }}/>
        <Text style={{
          width: '60%',
          color: '#cf118c',
          fontSize: '16pt',
          letterSpacing: '1.5pt',
          textAlign: 'center',
          fontWeight: 600
        }}>
          {dateString.toUpperCase()}
        </Text>
      </Page>
      <Page size="A4" style={{
        fontFamily: 'Omnes',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: 60
      }}>
        <View fixed style={{
          width: '90%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10pt'
        }}>
          <SVGRenderer src="/logo.svg" style={{ width: '70pt' }}/>
          <Text style={{ color: '#015ca7', fontSize: '10pt', letterSpacing: '1.5pt' }}>THE CRAYOLA CREATIVE STYLE TOOL</Text>
        </View>
        <View style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '12pt',
          width: '100%',
          marginTop: '20pt'
        }}>
          <Text style={{
            fontWeight: 700,
            fontStyle: 'italic',
            color: '#015ca7',
            fontSize: '28pt'
          }}>
            Your Results
          </Text>
          <SVGRenderer src='/green-brush.svg' style={{ width: '200pt', position: 'relative', bottom: '12pt' }}/>
          <View style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            zIndex: 9999,
            width: '100%',
            gap: '6pt'
          }}>
            <PDFViewControl
                headerText="How you think"
                result={thinkType}
            />
            <PDFViewControl
                headerText="How you perceive"
                result={perceiveType}
            />
            <PDFViewControl
                headerText="How you engage"
                result={engageType}
            />
          </View>
          <PDFResultText
            headerText="How you think"
            resultView={ResultsView.Think}
            result={thinkType}
          />
          <PDFResultText
            headerText="How you perceive"
            resultView={ResultsView.Perceive}
            result={perceiveType}
          />
          <PDFResultText
            headerText="How you engage"
            resultView={ResultsView.Engage}
            result={engageType}
          />
        </View>
        <View fixed style={{
          width: '90%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          bottom: 15,
          position: 'absolute'
        }}>
          <Text
            style={{ color: '#015ca7', fontSize: '10pt', letterSpacing: '1.5pt' }}
            render={({ pageNumber, totalPages }) => (
              `PG ${pageNumber} OF ${totalPages}`
            )}
          />
          <Text style={{ color: '#015ca7', fontSize: '10pt', letterSpacing: '1.5pt' }}>
            SEE MORE AT WWW.CRAYOLA.COM/CREATIVESTYLES
          </Text>
        </View>
      </Page>
    </Document>
  );
}